import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { InputText, createSellarColumnHelper } from "@sellar-io/components";
import { useNavigate } from "react-router";
import { InputContainer } from "@sellar-io/components";
import { useAvailabilityListStore } from "./availabilityListStore";
import { TableInfinite } from "../../components/table/TableInfinite";
import { capitalize, truncate } from "@sellar-io/common";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../components";
import { useMediaQuery } from "usehooks-ts";
import { twMerge } from "tailwind-merge";
import { useProductList } from "../../hooks/api/v2/product/useProductList";
import { useAvailabilityTierList } from "../../hooks/api/v2/availabilityTier/useAvailabilityTierList";
import { useProductAvailabilityTierUpdate } from "../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierUpdate";
import { useProductAvailabilityTierCreate } from "../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierCreate";
import {
  AvailabilityTierListResponseType,
  DeliveryMethodContainerRuleUnitType,
  ProductListResponseType,
} from "@sellar-io/api-v2/types";

import * as ROUTES from "../../constants/routes";
import { useDebounce } from "use-debounce";
import { isOrderItemEligibleForDeliveryMethod } from "@sellar-io/common";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import {
  Badge,
  BadgeBlue,
  BadgeGreen,
  BadgeZinc,
  BadgeAmber,
} from "../../components/badge/Badge";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../components/dropdown/Dropdown";
import { AvailabilityTierDeleteDialog } from "./components/AvailabilityTierDeleteDialog";
import { AvailabilityTierMap } from "./availabilityTierStore";
import { useFeatureFlagStore } from "../../hooks/useFeatureFlags";
import { StorefrontPreviewer } from "../../components/storefrontPreviewer/StorefrontPreviewer";
import {
  StorefrontPreviewerTabs,
  useStorefrontPreviewerParameters,
} from "../../components/storefrontPreviewer/StorefrontPreviewer.shared";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import { InactiveBadge } from "../../components/badge/InactiveBadge";
import { useFulfillmentTypeList } from "../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import {
  Anchor,
  Button,
  Card,
  DataButton,
  H4,
  ListItemIconTile,
  SingleFieldDialog,
  SingleFieldDialogImage,
  SingleFieldDialogHeader,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { useAvailabilityTierSetDefault } from "../../hooks/api/v2/availabilityTier/useAvailabilityTierSetDefault";
import { DefaultProductCatalogueTooltip } from "./components/DefaultProductCatalogueTooltip";
import { FilterPopover } from "../../components/filterPopover/FilterPopover";
import { AvailabilityListFilter } from "./AvailabilityListFilter";
import { Tag } from "../../components/tag/Tag";
import { useProductCatalogueMode } from "../../hooks/useProductCatalogueMode";
import { useAvailabilityTierModeUpdate } from "../../hooks/api/v2/availabilityTier/useAvailabilityTierModeUpdate";
import { Alert } from "~/components/alert/Alert";
import { EmptyStateCard } from "~/components/emptyStateCard/EmptyStateCard";

const isDeliveryMethodContainerRuleUnitType = (
  unit: string | null,
): unit is DeliveryMethodContainerRuleUnitType => {
  if (!unit) return false;

  const validUnits: DeliveryMethodContainerRuleUnitType[] = [
    "gal",
    "L",
    "ml",
    "cl",
    "pt",
    "oz",
    "g",
    "kg",
  ];

  return validUnits.includes(unit);
};

export const AvailabilityLists = () => {
  const remoteFeatureFlags = useRemoteFeatureFlags();
  const catalogueMode = useProductCatalogueMode();
  const isBasicProductCatalogueEnabled = remoteFeatureFlags.isActive(
    "basicProductCatalogueMode",
  );

  const showBasicMode = isBasicProductCatalogueEnabled;

  if (catalogueMode.isLoading && showBasicMode) return null;
  if (catalogueMode.isBasicMode && showBasicMode)
    return <BasicProductCatalogue />;

  return <AdvancedAvailabilityLists />;
};

const AdvancedAvailabilityLists = () => {
  const remoteFeatureFlags = useRemoteFeatureFlags();
  const isSupplierStorefrontFeatureFlagEnabled = useFeatureFlagStore().isActive(
    "SUPPLIER_STOREFRONT_PREVIEW",
  );
  const isUnbundlingEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );
  const isSellarDistributionEnabled = remoteFeatureFlags.isActive(
    "sellarDistributionBeta",
  );

  const tradeTermsPlaceholdersEnabled = useFeatureFlagStore().isActive(
    "TRADE_TERM_PLACEHOLDERS",
  );

  const isBasicProductCatalogueEnabled = remoteFeatureFlags.isActive(
    "basicProductCatalogueMode",
  );

  const showBasicMode = isBasicProductCatalogueEnabled;

  const {
    search,
    setSearch,
    productVisibility,
    setProductVisibility,
    containerRuleSelection,
    setContainerRuleSelection,
    toggleAvailability,
    availabilityMap,
    setAvailabilityMap,
  } = useAvailabilityListStore();

  const navigate = useNavigate();

  const [debouncedSearch] = useDebounce(search, 500);

  const [isTableView, setIsTableView] = useState(false);

  const [deleteDialogActive, setDeleteDialogActive] = useState(false);
  const [revertDialogActive, setRevertDialogActive] = useState(false);
  const [selectedTier, setSelectedTier] = useState<{
    id: number;
    name: string;
    isDefault: boolean;
  } | null>(null);

  const [setDefaultTarget, setSetDefaultTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const [visibleAvailabilityTiers, setVisibleAvailabilityTiers] = useState<
    number[]
  >([]);

  const [desktopFilterActive, setDesktopFilterActive] = useState(false);
  const [clickOutside, setClickOutside] = useState(true);

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const deliveryMethodsQuery = useFulfillmentTypeList(
    {},
    { enabled: isUnbundlingEnabled },
  );

  const deliveryMethods = useMemo(
    () =>
      deliveryMethodsQuery.data?.pages
        .flat()
        .filter((dm) => dm.fulfillmentType !== "sellarDistribution") ?? [],
    [deliveryMethodsQuery.data],
  );

  const hasSellarDistributionOption = isSellarDistributionEnabled;

  const selectedDeliveryMethod = useMemo(
    () =>
      containerRuleSelection.type === "deliveryMethod"
        ? deliveryMethods.find(
            (method) => method.id === containerRuleSelection.id,
          )
        : null,
    [containerRuleSelection, deliveryMethods],
  );

  const selectedContainerRulesDeliveryMethod = useMemo(() => {
    if (containerRuleSelection.type === "deliveryMethod") {
      if (!selectedDeliveryMethod) return null;

      return {
        fulfillmentType: selectedDeliveryMethod.fulfillmentType,
        DeliveryMethodContainerRules:
          selectedDeliveryMethod.DeliveryMethodContainerRules,
      };
    }

    return null;
  }, [containerRuleSelection, selectedDeliveryMethod]);

  const isProductEligibleForSelectedContainerRules = useCallback(
    (product: ProductListResponseType[number]) => {
      if (!selectedContainerRulesDeliveryMethod) return true;
      if (!product.containerType) return true;
      if (!isDeliveryMethodContainerRuleUnitType(product.unit)) return true;

      return isOrderItemEligibleForDeliveryMethod({
        deliveryMethod: selectedContainerRulesDeliveryMethod,
        item: {
          containerType: product.containerType,
          volume: product.volume,
          unit: product.unit,
        },
      });
    },
    [selectedContainerRulesDeliveryMethod],
  );

  const { data, fetchNextPage, isFetching } = useProductList({
    search: isTableView ? debouncedSearch : "",
    sortBy: "name",
    sortDir: "asc",
    query: [
      ...(productVisibility !== "all"
        ? [
            {
              type: "active",
              modifier: "EQUALS",
              value: productVisibility === "active" ? true : false,
            } as const,
          ]
        : []),
      {
        type: "showAllProducts",
        modifier: "EQUALS",
        value: true,
      },
    ],
  });

  const availabilityTiersQuery = useAvailabilityTierList({
    includeCounts: true,
    search: !isTableView ? debouncedSearch : "",
    limit: 10,
  });

  const availabilityTiers = availabilityTiersQuery.data;

  const productAvailabilityTierUpdate = useProductAvailabilityTierUpdate();

  const updateAvailability = useCallback(
    ({
      id,
      active,
      ProductId,
      AvailabilityTierId,
    }: {
      id: number;
      active: boolean;
      ProductId: number;
      AvailabilityTierId: number;
    }) => {
      productAvailabilityTierUpdate.mutate(
        { id, active },
        {
          onSuccess: () => {
            toggleAvailability(ProductId, AvailabilityTierId);
          },
        },
      );
    },
    [productAvailabilityTierUpdate, toggleAvailability],
  );

  const productAvailabilityTierCreate = useProductAvailabilityTierCreate();

  const createAvailability = useCallback(
    ({
      active,
      ProductId,
      AvailabilityTierId,
    }: {
      active: boolean;
      ProductId: number;
      AvailabilityTierId: number;
    }) => {
      productAvailabilityTierCreate.mutate(
        {
          active,
          ProductId,
          AvailabilityTierId,
        },
        {
          onSuccess: () => {
            toggleAvailability(ProductId, AvailabilityTierId);
          },
        },
      );
    },
    [productAvailabilityTierCreate, toggleAvailability],
  );

  // Flatten data
  const flatProducts = useMemo(() => {
    const parents: {
      [index: number]: ProductListResponseType[number] & {
        Products: ProductListResponseType;
      };
    } = {};
    const products: ProductListResponseType = [];

    if (!data) return [];

    // Separate parents and products
    data.pages.forEach((page) =>
      page.forEach((p) => {
        if (p.type !== "category") {
          if (p.ParentId) {
            products.push(p);
          } else {
            parents[p.id] = { ...p, Products: [] };
          }
        }
      }),
    );

    // Attach products to their parent
    products.forEach((p) => {
      if (p.ParentId && parents[p.ParentId]) {
        if (isProductEligibleForSelectedContainerRules(p)) {
          parents[p.ParentId]?.Products.push(p);
        }
      }
    });

    // Flatten again in desired order
    const result: ProductListResponseType = [];
    Object.values(parents).forEach((parent) => {
      if (
        selectedContainerRulesDeliveryMethod &&
        parent.Products.length === 0
      ) {
        return;
      }

      result.push(parent);
      parent.Products.forEach((p) => result.push(p));
    });

    return result;
  }, [
    data,
    isProductEligibleForSelectedContainerRules,
    selectedContainerRulesDeliveryMethod,
  ]);

  const flatTiers = useMemo(
    () => availabilityTiers?.pages.flatMap((a) => a) ?? [],
    [availabilityTiers],
  );

  const hasMultipleDefaults = useMemo(
    () =>
      [...flatTiers].some(
        (list) =>
          list.defaultType === "direct" ||
          list.defaultType === "sellarDistribution",
      ),
    [flatTiers],
  );

  const setDefaultMutation = useAvailabilityTierSetDefault();

  const columnHelper =
    createSellarColumnHelper<ProductListResponseType[number]>();

  // Build columns for TableInfinite
  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row, {
        id: "name",
        enableSorting: false,
        header: "Product",
        cell: (info) => {
          const row = info.getValue();
          const nameParts = row.name?.split(" • ");
          const childName = `${nameParts?.[1]}${nameParts?.[2] ? " • " : ""}${nameParts?.[2] ?? ""}`;
          return (
            <span className="flex items-center gap-2 overflow-hidden p-2">
              {!row.ParentId && (
                <ProfilePicture logoUrl={row.imageUrl} size="small" />
              )}
              <span
                className={twMerge(
                  "w-full overflow-hidden text-nowrap text-ellipsis",
                  row.ParentId ? "pl-8" : "font-semibold",
                )}
              >
                {row.ParentId ? childName : row.name}
              </span>
              {!row.ParentId && !row.active && (
                <InactiveBadge productId={row.id} />
              )}
            </span>
          );
        },
      }),
      ...flatTiers
        .filter(
          (t) =>
            visibleAvailabilityTiers.length === 0 ||
            visibleAvailabilityTiers.some((tier) => tier === t.id),
        )
        .map((tier) =>
          columnHelper.accessor((row) => row, {
            id: tier.name,
            enableSorting: false,
            header: () => (
              <span
                className="group flex w-full cursor-pointer items-center justify-between gap-2 text-left"
                onClick={() =>
                  navigate(
                    ROUTES.AVAILABILITY_TIER.href({
                      availabilityTierId: tier.id,
                    }),
                  )
                }
              >
                <span className="w-full overflow-hidden text-nowrap text-ellipsis">
                  {tier.name}{" "}
                  {tier.defaultType && (
                    <span className="pl-2 text-xs font-semibold text-zinc-500">
                      DEFAULT
                    </span>
                  )}
                </span>
                <Icon
                  icon="CHEVRON_RIGHT"
                  size={0.7}
                  className="flex-none text-zinc-400 group-hover:text-zinc-700"
                />
              </span>
            ),
            cell: (info) => {
              const row = info.getValue();
              return row.ParentId ? (
                <span
                  className={twMerge(
                    "w-full p-2",
                    !row.Parent?.active && "bg-zinc-50",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={!!availabilityMap[row.id]?.[tier.id]}
                    onChange={(e) => {
                      const hasProductAvailabilityTier =
                        row.ProductAvailabilityTiers.find(
                          (at) => at.AvailabilityTierId === tier.id,
                        );
                      if (!!hasProductAvailabilityTier) {
                        updateAvailability({
                          id: hasProductAvailabilityTier.id,
                          active: e.target.checked,
                          ProductId: row.id,
                          AvailabilityTierId: tier.id,
                        });
                      } else {
                        createAvailability({
                          active: e.target.checked,
                          ProductId: row.id,
                          AvailabilityTierId: tier.id,
                        });
                      }
                    }}
                  />
                </span>
              ) : (
                <span
                  className={twMerge(
                    "h-full w-full",
                    !row.ParentId && !row.active && "bg-zinc-50",
                  )}
                />
              );
            },
          }),
        ),
    ];
  }, [
    columnHelper,
    flatTiers,
    availabilityMap,
    createAvailability,
    navigate,
    updateAvailability,
    visibleAvailabilityTiers,
  ]);

  // Sync availability state
  useEffect(() => {
    const newMap: AvailabilityTierMap = {};
    flatProducts.forEach((p) => {
      if (!newMap[p.id])
        newMap[p.id] = Object.fromEntries(
          p.ProductAvailabilityTiers.map((a) => [
            a.AvailabilityTierId,
            a.active,
          ]),
        );
    });
    setAvailabilityMap(newMap);
  }, [flatProducts, setAvailabilityMap]);

  const showFilterTags =
    productVisibility !== "all" || containerRuleSelection.type !== "none";

  const containerRulesLabel = (() => {
    if (containerRuleSelection.type === "none") return "None";
    if (containerRuleSelection.type === "sellarDistribution") {
      return "Sellar distribution";
    }

    const selectedDeliveryMethod = deliveryMethods.find(
      (method) => method.id === containerRuleSelection.id,
    );

    return (
      selectedDeliveryMethod?.name ??
      capitalize(selectedDeliveryMethod?.fulfillmentType ?? "")
    );
  })();

  if (availabilityTiersQuery.isError) {
    return (
      <>
        <NavigationBarMenu title="Product catalogues" borderBottom={false} />
        <div className="mx-auto w-full max-w-300 px-4 py-8">
          <Alert
            type="error"
            title="Unable to load product catalogues"
            message="Please try loading your product catalogues again."
          >
            <Button
              onClick={() => availabilityTiersQuery.refetch()}
              loading={availabilityTiersQuery.isFetching}
            >
              Try again
            </Button>
          </Alert>
        </div>
      </>
    );
  }

  if (!availabilityTiersQuery.isSuccess) {
    return <SpinnerLegacy />;
  }

  return (
    <>
      <NavigationBarMenu
        title="Product catalogues"
        borderBottom={false}
        rightComponent={
          isSupplierStorefrontFeatureFlagEnabled ? (
            <StorefrontPreviewButton />
          ) : null
        }
      />
      <div className="mx-auto flex max-w-300 items-center gap-2">
        <div
          className={twMerge(
            "mb-24 flex w-full flex-col gap-4 px-4",
            isDesktop ? "mt-10" : "mt-2",
          )}
        >
          <div className="flex flex-col gap-1 border-b border-zinc-300 pb-4">
            <H4 className="font-semibold">Product catalogues</H4>
            <p>
              Product catalogues control what your customers can and cannot see
              on your storefront. Product catalogues are defined independently
              to the pricing that your customers see. For more information or
              help setting up your product catalogues, visit our{" "}
              <Anchor
                className="inline-flex items-center gap-1 text-blue-600 underline"
                href="https://intercom.help/sellario/en/articles/12008469-supplier-guide-product-catalogues"
                target="_blank"
                rel="noopener noreferrer"
              >
                help centre
                <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
              </Anchor>
              .
            </p>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex">
                  <InputContainer
                    className={twMerge(
                      "w-62.5 flex-none py-[0.4rem]",
                      isTableView && "rounded-r-none",
                    )}
                  >
                    <InputText
                      required={false}
                      placeholder={
                        isTableView
                          ? "Search products..."
                          : "Search product catalogues..."
                      }
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full"
                    />
                  </InputContainer>
                  {isTableView && (
                    <Listbox
                      value={visibleAvailabilityTiers}
                      onChange={setVisibleAvailabilityTiers}
                      multiple
                    >
                      <ListboxButton
                        className={
                          "relative flex w-35 items-center overflow-hidden rounded-r-lg border border-l-0 border-solid bg-white px-4 py-2 pr-12 text-left text-sm font-medium text-ellipsis whitespace-nowrap shadow-xs"
                        }
                      >
                        {visibleAvailabilityTiers.length
                          ? truncate(
                              visibleAvailabilityTiers
                                .map(
                                  (tier) =>
                                    flatTiers.find((t) => t.id === tier)?.name,
                                )
                                .join(", "),
                            )
                          : "All catalogs"}
                        <Icon
                          icon="CHEVRON_DOWN"
                          size={0.8}
                          className="absolute right-2"
                        />
                      </ListboxButton>
                      <ListboxOptions
                        className={
                          "mt-1 w-[190.3px] rounded-lg border border-solid bg-white p-2 px-4 text-left text-sm shadow-xs"
                        }
                        anchor="bottom start"
                      >
                        <ListboxOption
                          key={"x1"}
                          value={false}
                          disabled={true}
                          className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 text-zinc-500 active:bg-zinc-50 data-focus:bg-blue-100"
                        >
                          Select multiple...
                        </ListboxOption>
                        {flatTiers.map((tier) => (
                          <ListboxOption
                            key={tier.id}
                            value={tier.id}
                            className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 active:bg-zinc-50 data-focus:bg-blue-100"
                          >
                            {visibleAvailabilityTiers.includes(tier.id) ? (
                              <Icon icon="CHECK" size={0.7} />
                            ) : (
                              <span className="w-[16.8px]" />
                            )}
                            {tier.name}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Listbox>
                  )}
                </div>

                {isDesktop && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center rounded-lg border border-zinc-300 shadow-[0_2px_2px_0_rgba(0,0,0,0.15)]">
                      <Button
                        className={twMerge(
                          "flex items-center gap-1 rounded-r-none border-r border-solid border-zinc-400 text-sm font-medium normal-case",
                          isTableView ? "bg-white" : "bg-zinc-200",
                        )}
                        color="transparent"
                        size="small"
                        onClick={() => setIsTableView(false)}
                      >
                        <Icon icon="FORMAT_LIST_BULLETED" />
                        List view
                      </Button>
                      <Button
                        className={twMerge(
                          "flex items-center gap-1 rounded-l-none text-sm font-medium normal-case",
                          isTableView ? "bg-zinc-200" : "bg-white",
                        )}
                        color="transparent"
                        size="small"
                        onClick={() => setIsTableView(true)}
                      >
                        <Icon icon="GRID" />
                        Table view
                      </Button>
                    </div>
                    {isTableView && isUnbundlingEnabled && (
                      <FilterPopover
                        icon="FILTER_VARIANT"
                        buttonText="Filter"
                        compact={false}
                        active={desktopFilterActive}
                        toggleActive={setDesktopFilterActive}
                        clickOutside={clickOutside}
                        contentClassName="min-w-[400px]"
                      >
                        <AvailabilityListFilter
                          onDismiss={() => setDesktopFilterActive(false)}
                          setClickOutside={(value?: boolean) =>
                            setClickOutside(value ?? !clickOutside)
                          }
                          deliveryMethods={deliveryMethods}
                          hasSellarDistributionOption={
                            hasSellarDistributionOption
                          }
                        />
                      </FilterPopover>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <DataButton
                className="flex items-center gap-1"
                border={true}
                onClick={() => navigate(ROUTES.AVAILABILITY_TIER_NEW)}
              >
                <Icon icon="PLUS" />
                New
              </DataButton>
              {showBasicMode && (
                <Dropdown>
                  <DropdownButton>
                    <DataButton className="py-2.5" border={true}>
                      <Icon
                        icon={"DOTS_VERTICAL"}
                        className="text-black"
                        size={0.7}
                      />
                    </DataButton>
                  </DropdownButton>
                  <DropdownItems>
                    <DropdownItem
                      className="text-sm text-red-700"
                      onClick={() => setRevertDialogActive(true)}
                    >
                      Revert back to one catalogue
                    </DropdownItem>
                  </DropdownItems>
                </Dropdown>
              )}
            </div>
          </div>

          {isDesktop && isTableView && (
            <>
              {showFilterTags && (
                <div className="flex w-full items-center gap-2">
                  {productVisibility !== "all" && (
                    <Tag
                      className="mr-2"
                      onClick={() => setProductVisibility("all")}
                      icon={productVisibility === "active" ? "EYE" : "EYE_OFF"}
                    >
                      {productVisibility === "active" ? "Active" : "Inactive"}{" "}
                      products only
                    </Tag>
                  )}
                  {containerRuleSelection.type !== "none" && (
                    <Tag
                      className="mr-2"
                      onClick={() =>
                        setContainerRuleSelection({ type: "none" })
                      }
                      icon="KEG"
                    >
                      {containerRulesLabel}
                    </Tag>
                  )}
                </div>
              )}

              <TableInfinite
                columns={columns}
                data={flatProducts}
                infiniteQuery={{
                  data: flatProducts,
                  fetchNextPage,
                  isFetching,
                  totalCount: 10000,
                }}
                noResultsText="No products found."
                horizontalInfinite={true}
                gridClasses={`grid-cols-[minmax(300px,_1fr)_repeat(${columns.length - 1},_170px)]`}
                gridStyles={{
                  gridTemplateColumns: `minmax(300px, 1fr) repeat(${columns.length - 1}, minmax(170px, 1fr))`,
                }}
                roundedTop={true}
              />
            </>
          )}
          {(!isDesktop || !isTableView) && (
            <InfiniteList
              query={availabilityTiersQuery}
              className="mb-8 flex flex-col gap-2"
            >
              {flatTiers.map((tier, idx) => (
                <ListItem
                  key={idx}
                  name={`${tier.name}`}
                  defaultType={tier.defaultType}
                  numberOfCustomers={tier.numberOfCustomers}
                  hiddenProducts={tier.hiddenProducts}
                  onClick={() =>
                    navigate(
                      ROUTES.AVAILABILITY_TIER.href({
                        availabilityTierId: tier.id,
                      }),
                    )
                  }
                  hasMultipleDefaults={hasMultipleDefaults}
                  rightComponent={
                    <Dropdown>
                      <DropdownButton>
                        <Button size={"small"} color={"transparent"}>
                          <Icon
                            icon={"PENCIL"}
                            className="text-black"
                            size={0.7}
                          />
                        </Button>
                      </DropdownButton>
                      <DropdownItems>
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm"
                          onClick={() =>
                            navigate(
                              ROUTES.AVAILABILITY_TIER.href({
                                availabilityTierId: tier.id,
                              }),
                            )
                          }
                        >
                          Edit
                        </DropdownItem>
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm"
                          onClick={() =>
                            navigate(
                              ROUTES.AVAILABILITY_TIER.href({
                                availabilityTierId: tier.id,
                              }),
                              { state: { launchAssignmentDialog: true } },
                            )
                          }
                        >
                          Assign
                        </DropdownItem>
                        {isUnbundlingEnabled && (
                          <DropdownItem
                            className={twMerge(
                              "border-b border-zinc-200 text-sm",
                              tier.defaultType &&
                                !hasMultipleDefaults &&
                                "cursor-not-allowed opacity-50",
                            )}
                            onClick={() => {
                              if (tier.defaultType && !hasMultipleDefaults) {
                                return;
                              }

                              setSetDefaultTarget({
                                id: tier.id,
                                name: tier.name,
                              });
                            }}
                          >
                            Mark as default
                          </DropdownItem>
                        )}
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm text-red-700"
                          onClick={() => {
                            setSelectedTier({
                              id: tier.id,
                              name: tier.name,
                              isDefault: Boolean(tier.defaultType),
                            });
                            setDeleteDialogActive(true);
                          }}
                        >
                          Delete
                        </DropdownItem>
                      </DropdownItems>
                    </Dropdown>
                  }
                />
              ))}
              {flatTiers.length === 0 && tradeTermsPlaceholdersEnabled && (
                <PlaceholderListItem
                  onClick={() => navigate(ROUTES.AVAILABILITY_TIER_NEW)}
                />
              )}
            </InfiniteList>
          )}
        </div>
        {isDesktop && isTableView && columns.length > 4 && (
          <Icon icon="CHEVRON_RIGHT" className="mt-24 flex-none" />
        )}
      </div>
      {selectedTier && (
        <AvailabilityTierDeleteDialog
          availabilityTierId={selectedTier.id}
          availabilityTierName={selectedTier.name}
          isDefault={selectedTier.isDefault}
          active={deleteDialogActive}
          setActive={setDeleteDialogActive}
        />
      )}
      <SetDefaultAvailabilityTierDialog
        setDefaultTarget={setDefaultTarget}
        onDismiss={() => setSetDefaultTarget(null)}
        hasMultipleDefaults={hasMultipleDefaults}
        isPending={setDefaultMutation.isPending}
        onConfirm={() => {
          if (!setDefaultTarget) return;

          setDefaultMutation.mutate(
            { id: setDefaultTarget.id },
            { onSuccess: () => setSetDefaultTarget(null) },
          );
        }}
      />
      <RevertToBasicCatalogueDialog
        active={revertDialogActive}
        onDismiss={() => setRevertDialogActive(false)}
        catalogueCount={flatTiers.length}
      />
    </>
  );
};

const BasicProductCatalogue = () => {
  const [setupDialogPage, setSetupDialogPage] = useState<
    "introduction" | "setup" | null
  >(null);
  const modeMutation = useAvailabilityTierModeUpdate();

  const dismissSetupDialog = () => setSetupDialogPage(null);

  return (
    <>
      <NavigationBarMenu title="Product catalogues" borderBottom={false} />
      <EmptyStateCard
        badge={<BadgeBlue className="shrink-0">Optional feature</BadgeBlue>}
        title="Show different products to different customers"
        description="Need to provide some customers with a bespoke product availability
              list? You can optionally create different product catalogues to
              control which customers see which products."
      >
        <div className="flex flex-col gap-4">
          <Alert
            type="success"
            title="All customers currently see your full range"
            message="Your full active product range is shown to every customer unless
                otherwise specified on a product catalogue."
          />
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-zinc-700">
              Learn more about how product catalogues work before turning it on.
            </p>
            <Button onClick={() => setSetupDialogPage("introduction")}>
              Learn more
            </Button>
          </div>
        </div>
      </EmptyStateCard>

      <SingleFieldDialog
        key={setupDialogPage}
        active={setupDialogPage !== null}
        onDismiss={dismissSetupDialog}
        size="large"
      >
        {setupDialogPage === "introduction" ? (
          <>
            <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-pc-1.png" />
            <SingleFieldDialogHeader
              title="Product catalogues control which customers see which products"
              description="You can use product catalogues to provide chosen customers with a more bespoke availability list. Some examples of how you might want to use them are shown above."
              icon="INFORMATION_OUTLINE"
              iconClassName="text-blue-600 bg-blue-50 mb-auto"
            />
            <SingleFieldDialogCTA>
              <Button color="transparent" onClick={dismissSetupDialog}>
                Dismiss
              </Button>
              <Button
                className="flex items-center gap-1"
                onClick={() => setSetupDialogPage("setup")}
              >
                Next <Icon icon="CHEVRON_RIGHT" size={0.8} />
              </Button>
            </SingleFieldDialogCTA>
          </>
        ) : (
          <>
            <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-pc-2.png" />
            <SingleFieldDialogHeader
              title="Set up product catalogues"
              description="Create product catalogues and assign them to one or several of your customers. Choose a default product catalogue that all customers, including new customers, are shown unless you assign them something specific."
              icon="INFORMATION_OUTLINE"
              iconClassName="text-blue-600 bg-blue-50 mb-auto"
            />
            <SingleFieldDialogCTA>
              <Button
                className="mr-auto flex items-center gap-1"
                color="transparent"
                onClick={() => setSetupDialogPage("introduction")}
              >
                <Icon icon="CHEVRON_LEFT" size={0.8} /> Back
              </Button>
              <Button
                loading={modeMutation.isPending}
                onClick={() =>
                  modeMutation.mutate(
                    { mode: "advanced" },
                    { onSuccess: dismissSetupDialog },
                  )
                }
              >
                Continue to set up
              </Button>
            </SingleFieldDialogCTA>
          </>
        )}
      </SingleFieldDialog>
    </>
  );
};

const RevertToBasicCatalogueDialog = ({
  active,
  onDismiss,
  catalogueCount,
}: {
  active: boolean;
  onDismiss: () => void;
  catalogueCount: number;
}) => {
  const [page, setPage] = useState<"introduction" | "blocked" | "confirm">(
    "introduction",
  );
  const [confirmed, setConfirmed] = useState(false);
  const modeMutation = useAvailabilityTierModeUpdate();
  const canRevert = catalogueCount === 1;

  const dismiss = () => {
    setPage("introduction");
    setConfirmed(false);
    onDismiss();
  };

  if (page === "introduction") {
    return (
      <SingleFieldDialog
        key={`${active}-introduction`}
        active={active}
        onDismiss={dismiss}
        size="large"
      >
        <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-pc-3.png" />
        <SingleFieldDialogHeader
          title="You can revert back to one product catalogue for all customers"
          description="If you no longer need the functionality of bespoke product catalogues for different customers, you can go back to just having one product catalogue which shows all of your products to all of your customers."
          icon="INFORMATION_OUTLINE"
          iconClassName="text-blue-600 bg-blue-50 mb-auto"
        />
        <SingleFieldDialogCTA>
          <Button color="transparent" onClick={dismiss}>
            Dismiss
          </Button>
          <Button
            className="flex items-center gap-1"
            onClick={() => setPage(canRevert ? "confirm" : "blocked")}
          >
            Next <Icon icon="CHEVRON_RIGHT" size={0.8} />
          </Button>
        </SingleFieldDialogCTA>
      </SingleFieldDialog>
    );
  }

  if (page === "blocked") {
    return (
      <SingleFieldDialog
        key={`${active}-blocked`}
        active={active}
        onDismiss={dismiss}
        size="large"
      >
        <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-pc-4.png" />
        <SingleFieldDialogHeader
          title="You need to delete your bespoke product catalogues"
          description="Delete your bespoke product catalogues and re-assign the customers currently assigned to them in order to revert to a one product catalogue set-up."
          icon="INFORMATION_OUTLINE"
          iconClassName="text-blue-600 bg-blue-50 mb-auto"
        />
        <SingleFieldDialogCTA>
          <Button
            className="mr-auto flex items-center gap-1"
            color="transparent"
            onClick={() => setPage("introduction")}
          >
            <Icon icon="CHEVRON_LEFT" size={0.8} /> Back
          </Button>
          <Button onClick={dismiss}>I understand</Button>
        </SingleFieldDialogCTA>
      </SingleFieldDialog>
    );
  }

  return (
    <SingleFieldDialog
      key={`${active}-confirm`}
      active={active}
      onDismiss={dismiss}
      size="large"
    >
      <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-pc-5.png" />
      <SingleFieldDialogHeader
        title="Revert back to one product catalogue"
        description="Confirm your choice to revert to just one product catalogue which shows all products to all customers."
        icon="INFORMATION"
        iconClassName="text-blue-600 bg-blue-50 mb-auto"
      />
      <label className="mx-4 flex cursor-pointer items-start gap-2">
        <input
          className="mt-1"
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        <span>I confirm I want to revert back to one product catalogue</span>
      </label>
      <SingleFieldDialogCTA>
        <Button
          className="mr-auto flex items-center gap-1"
          color="transparent"
          onClick={() => setPage("introduction")}
        >
          <Icon icon="CHEVRON_LEFT" size={0.8} /> Back
        </Button>
        <Button
          disabled={!confirmed}
          loading={modeMutation.isPending}
          onClick={() =>
            modeMutation.mutate({ mode: "basic" }, { onSuccess: dismiss })
          }
        >
          Confirm
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};

const SetDefaultAvailabilityTierDialog = ({
  setDefaultTarget,
  onDismiss,
  hasMultipleDefaults,
  isPending,
  onConfirm,
}: {
  setDefaultTarget: { id: number; name: string } | null;
  onDismiss: () => void;
  hasMultipleDefaults: boolean;
  isPending: boolean;
  onConfirm: () => void;
}) => {
  const [checked, setChecked] = useState(false);

  return (
    <SingleFieldDialog
      key={setDefaultTarget?.id ?? "no-target"}
      active={!!setDefaultTarget}
      onDismiss={onDismiss}
      size={hasMultipleDefaults ? "large" : "small"}
    >
      {hasMultipleDefaults && (
        <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/terms-basic-advanced/basic-pc-1.png" />
      )}
      <SingleFieldDialogHeader
        title={`Are you sure you want to make '${setDefaultTarget?.name}' your default product catalogue?`}
        description={
          hasMultipleDefaults ? (
            <p>
              You will no longer have different default product catalogues for
              Local and Nationwide customers. All new customers will see '
              {setDefaultTarget?.name}' availability on your storefront. You can
              set which container types they see conditionally based on their
              delivery method chosen.
              <br />
              <br />
              <Anchor
                className="inline-flex gap-1 text-blue-600 underline"
                href="https://intercom.help/sellario/en/articles/12011312-supplier-guide-customer-zones"
                target="_blank"
                rel="noopener noreferrer"
              >
                Why should I only have one default product catalogue?
                <Icon
                  className="no-underline"
                  icon="ARROW_TOP_RIGHT"
                  size={0.6}
                />
              </Anchor>
            </p>
          ) : (
            "This will be the product catalogue shown to all new customers on the Market who haven't been explicitly assigned any custom product catalogue"
          )
        }
        icon="INFORMATION"
        iconClassName="text-blue-600 bg-blue-50 mb-auto"
      />
      <SingleFieldDialogCTA className="items-center">
        <label className="mr-auto ml-4 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          I understand
        </label>

        <Button color="transparent" onClick={onDismiss}>
          Dismiss
        </Button>
        <Button onClick={onConfirm} loading={isPending} disabled={!checked}>
          Confirm
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};

const ListItem = ({
  name,
  defaultType,
  numberOfCustomers,
  hiddenProducts,
  onClick,
  hasMultipleDefaults,
  rightComponent,
}: {
  name: string;
  defaultType: AvailabilityTierListResponseType[number]["defaultType"];
  numberOfCustomers: number | null;
  hiddenProducts: number | null;
  onClick: () => void;
  hasMultipleDefaults: boolean;
  rightComponent?: ReactNode;
}) => {
  return (
    <Card
      onClick={onClick}
      className="flex cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
    >
      <ListItemIconTile icon="FORMAT_LIST_BULLETED" />
      <div className="flex w-full items-center gap-4 md:flex-nowrap">
        <div className="flex flex-col">
          <p className="font-semibold">{name}</p>
          {hiddenProducts !== null && (
            <p className="text-sm">{hiddenProducts} product items hidden</p>
          )}
        </div>
        <span className="ml-auto flex flex-none items-center gap-2">
          {defaultType === "default" && (
            <DefaultProductCatalogueTooltip
              hasMultipleDefaults={hasMultipleDefaults}
            >
              <Badge>
                {hasMultipleDefaults ? (
                  <span className="flex items-center gap-1">
                    <Icon icon="INFORMATION" size={0.6} />
                    Nationwide default
                  </span>
                ) : (
                  "Default"
                )}
              </Badge>
            </DefaultProductCatalogueTooltip>
          )}
          {defaultType === "direct" && (
            <DefaultProductCatalogueTooltip
              hasMultipleDefaults={hasMultipleDefaults}
            >
              <Badge>
                <span className="flex items-center gap-1">
                  <Icon icon="INFORMATION" size={0.6} />
                  Local default
                </span>
              </Badge>
            </DefaultProductCatalogueTooltip>
          )}
          {defaultType === "sellarDistribution" && (
            <DefaultProductCatalogueTooltip
              hasMultipleDefaults={hasMultipleDefaults}
            >
              <Badge>
                <span className="flex items-center gap-1">
                  <Icon icon="INFORMATION" size={0.6} />
                  Sellar Distribution default
                </span>
              </Badge>
            </DefaultProductCatalogueTooltip>
          )}
          {numberOfCustomers !== null &&
            (numberOfCustomers === 0 ? (
              <BadgeZinc icon="ACCOUNT_MULTIPLE">0</BadgeZinc>
            ) : (
              <BadgeGreen icon="ACCOUNT_MULTIPLE">
                {numberOfCustomers}
              </BadgeGreen>
            ))}
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => e.stopPropagation()}
          >
            {rightComponent ?? <Icon icon="CHEVRON_RIGHT" size={1} />}
          </div>
        </span>
      </div>
    </Card>
  );
};

const PlaceholderListItem = ({ onClick }: { onClick: () => void }) => {
  return (
    <Card
      onClick={onClick}
      className="flex cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
    >
      <ListItemIconTile icon="FORMAT_LIST_BULLETED" />
      <div className="flex w-full items-center gap-4 md:flex-nowrap">
        <div className="flex flex-col">
          <p className="flex items-center gap-2 font-semibold">
            First product catalogue <BadgeAmber>Setup now</BadgeAmber>
          </p>
          <p className="text-sm">Click to setup your first product catalogue</p>
        </div>
        <span className="ml-auto flex flex-none items-center gap-2">
          <BadgeZinc icon="ACCOUNT_MULTIPLE">0</BadgeZinc>
          <div>
            <Icon icon="CHEVRON_RIGHT" size={1} />
          </div>
        </span>
      </div>
    </Card>
  );
};

const StorefrontPreviewButton = () => {
  const widthLessThan640 = useMediaQuery("(max-width: 640px)");
  const {
    openStorefrontPreviewer,
    businessName,
    featureFlags,
    customerZones,
    isSearchingForCustomer,
    isSearchingForCustomerZone,
    customers,
    orderRule,
    selectedCustomer,
    productListQuery,
    tab,
    selectedCustomerZone,
    setSelectedCustomerZone,
    setOpenStorefrontPreviewer,
    setSelectedCustomer,
    setCustomerSearchTerm,
    setCustomerZoneSearchTerm,
    onChangeTab,
    onPreviewStorefront,
  } = useStorefrontPreviewerParameters({
    initialTab: StorefrontPreviewerTabs.CUSTOMER,
  });

  return (
    <StorefrontPreviewer
      open={openStorefrontPreviewer}
      initialTab={tab}
      trigger={
        <Button
          onClick={() => setOpenStorefrontPreviewer(true)}
          color="secondary"
          className="p-2"
        >
          <Icon icon="STOREFRONT_OUTLINE" />
          {!widthLessThan640 && <span>Preview</span>}
        </Button>
      }
      businessName={businessName}
      featureFlags={featureFlags}
      customerZoneSearchProps={{
        customerZones,
        isSearchingForCustomerZone,
        selectedCustomerZone,
        onSelectCustomerZone: setSelectedCustomerZone,
        onSearchForCustomerZone: setCustomerZoneSearchTerm,
      }}
      customerSearchProps={{
        customers,
        isSearchingForCustomer,
        selectedCustomer,
        onSelectCustomer: setSelectedCustomer,
        onSearchForCustomer: setCustomerSearchTerm,
      }}
      onTogglePreviewer={setOpenStorefrontPreviewer}
      orderRule={{
        name: orderRule?.name ?? "",
        minimumOrderValue: orderRule?.minimumOrderPrice ?? 0,
        minimumOrderQuantities: orderRule?.PriceTierOrderRules ?? [],
      }}
      productListQuery={productListQuery}
      onClickStorefrontPreview={onPreviewStorefront}
      onChangeTab={onChangeTab}
    />
  );
};
